import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Styled from "../../../CompanyForm.styled";
import * as MUIStyled from "../../../MUIStyled";
import {
    getFormReview,
    postFormReview,
    postContactDetail,
} from "../../../config/fetchAPI";
import { DynamicReviewSection } from "./DynamicReviewSection";
import { DefaultLayout } from "../../common/Layout/DefaultLayout";
import { CustomReviewDetail } from "./CustomReviewDetail";
import { BackButton } from "../../elements/Buttons/Buttons.js";
import { ButtonPurple } from "../../elements/Buttons/Buttons.js";
import ProgressBarStepper from "../../elements/ProgressBar/ProgressBar";
import CustomAlert from "../../elements/Alert/Alert";
import Loading from "../../elements/Loading/Loading.js";

const currentStep = 6;
const totalSteps = 6;

export const ReviewPage = () => {
    const [resultFormReview, setResultFormReview] = useState([]);
    const [reviewCompany, setReviewCompany] = useState([]);
    const [reviewAccount, setReviewAccount] = useState([]);
    const [reviewContact, setReviewContact] = useState([]);
    const [reviewPostalAddress, setReviewPostalAddress] = useState([]);
    const [reviewTradingAddress, setReviewTradingAddress] = useState([]);
    const [dropdownLabels, setDropdownLabels] = useState([]);

    const [areButtonsDisabled, setAreButtonsDisabled] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [reviewError, setReviewError] = useState("");

    const navigate = useNavigate();
    const associations = sessionStorage.getItem("associations");

    useEffect(() => {
        handleGetFormReview();
        // eslint-disable-next-line
    }, []);

    const handleGetFormReview = async () => {
        try {
            let resultData = await getFormReview();
            setResultFormReview(resultData);
            setReviewCompany(resultData.company);
            setReviewAccount(resultData.account);
            setReviewContact(resultData.contacts);
            setReviewPostalAddress(resultData.postalAddress);
            setReviewTradingAddress(resultData.tradingAddress);
            setDropdownLabels(JSON.parse(associations));
        } catch (error) {
            console.error("Error loading cached fields:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePostFormReview = async () => {
        try {
            // check if new contacts have been updated to id:0
            let resultData = await getFormReview();

            if (resultData) {
                let postResultData = await postFormReview();

                // Check if response contains an error
                if (postResultData?.error) {
                    const errorMessage = postResultData.errors
                        ? `${postResultData.error}\n${postResultData.errors.join(", ")}`
                        : postResultData.error;
                    setReviewError(errorMessage);
                    setIsLoading(false);
                    return false; // Indicate failure
                } else {
                    setReviewError("");
                    console.log("Form review posted successfully");
                    return true; // Indicate success
                }
            }
            return false; // If no resultData, return failure
        } catch (error) {
            console.error("Error posting form review:", error);
            setReviewError("An error occurred while submitting the form");
            setIsLoading(false);
            return false; // Indicate failure
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        // Convert Contacts temp IDs to 0
        if (Array.isArray(resultFormReview.contacts)) {
            const newContactsIds = resultFormReview.contacts.map((contact) =>
                contact.id < 10 ? { ...contact, id: 0 } : contact
            );
            await postContactDetail(newContactsIds);
        }

        const isSuccess = await handlePostFormReview();

        if (isSuccess) {
            setIsLoading(false);
            navigate("/thankyou");
        }
    };

    const renderAccountInfo = () => {
        if (reviewAccount.paymentMethod === "EFT") {
            return (
                <CustomReviewDetail
                    title="Account Information"
                    data=""
                    dataType={reviewAccount.paymentMethod}
                    onEdit={handleEditAccountInfo}
                />
            );
        } else {
            return (
                <CustomReviewDetail
                    title="Account Information"
                    data={reviewAccount}
                    dataType={reviewAccount.paymentMethod}
                    onEdit={handleEditAccountInfo}
                    areButtonsDisabled={areButtonsDisabled}
                />
            );
        }
    };

    // Handle editing mode
    const handleEditCompanyDetails = () => {
        navigate("/companyDetail");
    };

    const handleEditAddresses = () => {
        navigate("/trading");
    };

    const handleEditContactDetails = () => {
        navigate("/contactDetail");
    };

    const handleEditAccountInfo = () => {
        navigate("/paymentInfo");
    };

    const handleBack = () => {
        navigate("/paymentInfo");
    };

    const handleFormComplete = useCallback(
        () => setAreButtonsDisabled(true),
        []
    );

    return (
        <DefaultLayout
            currentStep={currentStep}
            onFormComplete={handleFormComplete}>
            <form>
                {isLoading ? (
                    <Loading />
                ) : (
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <Styled.ReviewContainer>
                            <Styled.MainTitleArrowWrapper
                                style={{ marginBottom: "20px" }}>
                                {!areButtonsDisabled && (
                                    <BackButton
                                        onClick={handleBack}
                                        sx={{ marginLeft: "10%" }}
                                    />
                                )}

                                <Styled.MainTitle
                                    style={{ margin: "8px 0 0 0" }}>
                                    Review company details
                                </Styled.MainTitle>
                            </Styled.MainTitleArrowWrapper>

                            <Styled.BodyText style={{ margin: "0 0 40px 0" }}>
                                Please review all details before submitting.
                            </Styled.BodyText>

                            <CustomReviewDetail
                                title="Company Details"
                                data={reviewCompany}
                                dropdownLabels={dropdownLabels}
                                onEdit={handleEditCompanyDetails}
                                areButtonsDisabled={areButtonsDisabled}
                            />

                            <DynamicReviewSection
                                title="Company Addresses"
                                tradingAddress={reviewTradingAddress}
                                postalAddress={reviewPostalAddress}
                                onEdit={handleEditAddresses}
                                areButtonsDisabled={areButtonsDisabled}
                            />

                            <DynamicReviewSection
                                title="Contact Details"
                                contactInfo={reviewContact}
                                onEdit={handleEditContactDetails}
                                areButtonsDisabled={areButtonsDisabled}
                            />

                            {/* account info */}
                            {renderAccountInfo()}

                            {reviewError && (
                                <CustomAlert message={reviewError} />
                            )}

                            {!areButtonsDisabled && (
                                <Styled.CustomSubmitContainer>
                                    <ButtonPurple
                                        type="submit"
                                        onClick={handleSubmit}
                                        style={{ width: "80%" }}>
                                        {isLoading ? (
                                            <MUIStyled.CustomCircularProgress
                                                size={24}
                                            />
                                        ) : (
                                            "Submit"
                                        )}
                                    </ButtonPurple>
                                </Styled.CustomSubmitContainer>
                            )}
                        </Styled.ReviewContainer>

                        <Styled.ProgressBarContainerWrapper>
                            {currentStep <= totalSteps && (
                                <ProgressBarStepper
                                    currentStep={currentStep}
                                    totalSteps={totalSteps}
                                />
                            )}
                        </Styled.ProgressBarContainerWrapper>
                    </div>
                )}
            </form>
        </DefaultLayout>
    );
};
